<template>
  <v-container class="py-0">
    <v-row>
      <v-col cols="4" class="px-0">
        <v-text-field v-model="search" class="mr-7" label="Pesquisar Arquivos" clear-icon="mdi-close-circle-outline" />
        <v-treeview
          class="treeview-bulldozerr"
          :search="search"
          :active.sync="selectedFolder"
          :open.sync="initiallyOpen"
          :items="items"
          item-key="id"
          shaped
          :activatable="activatable"
        >
          <template v-slot:prepend="{ item }">
            <div v-if="!item.file">
              <v-icon>mdi-folder-open</v-icon>
            </div>
            <div v-else :title="item.name">
              <v-icon>
                {{ files[item.file] }}
              </v-icon>
            </div>
          </template>
          <template v-slot:append="{ item }">
            <v-icon v-if="item.file" size="25" @click="deleteFile(item)">mdi-alpha-x</v-icon>
            <v-progress-circular v-if="!activatable && isLoading(item)" indeterminate color="primary" size="20" />
          </template>
          <template v-slot:label="{ item }">
            <div v-if="item.file" :class="item.filesize == 0 ? 'text-warning-bulldozer' : ''">
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <div style="font-size: 13px" v-bind="attrs" v-on="on">
                    {{ item.filesize == 0 ? '(0 KB) - ' : '' }} {{ item.name }}
                  </div>
                </template>
                <span>{{ item.name }}</span>
              </v-tooltip>
            </div>
            <div v-else>
              {{ item.name }}
            </div>
          </template>
        </v-treeview>
      </v-col>
      <v-col cols="8" class="px-0 py-0">
        <div class="d-flex">
          <v-divider vertical class="mr-6" />
          <div style="width: 100% !important; height: 100% !important">
            <div class="display-1 font-weight-normal mb-6 text-center">Vamos subir os arquivos necessários para o cálculo</div>
            <v-alert v-if="selectedFolder[0]" dense outlined type="info" color="blue" class="mb-0 px-1 py-1">
              Arquivos permitidos:
              <b>zip</b>
              .
              <br />
              Tamanho máximo dos arquivos:
              <b>{{ uploadMaxFilesize }}</b>
              .
              <br />
              Ao enviar arquivos compactados
              <b>(ZIP)</b>
              , assegure-se de que incluam
              <b>somente arquivos TXT e CSV</b>
              ,
              <b>excluindo subpastas, arquivos ZIP extras ou outros formatos</b>
              .
            </v-alert>
            <upload-files
              v-if="selectedFolder[0]"
              :extensoes-permitidas="['.zip']"
              :custom-function-upload="functionUpload"
              :caminho-tmp="caminhoTmp"
              :upload-files-reset="true"
              @file:uploaded="handleFileUploaded"
              @file:upload-in-progress="activatable = !$event"
            />
            <div v-else style="outline: 2px dashed grey !important">
              <v-row justify="center" align="center" class="py-4">
                <v-icon color="gray" size="96">mdi-block-helper</v-icon>
              </v-row>
              <p class="c-gray display-2 pb-4 text-center">Selecione uma pasta para fazer o upload do arquivo</p>
            </div>
          </div>
        </div>
        <div v-if="arquivosTamanhoZeroIdentificados" class="ml-6">
          <v-alert icon="mdi-alert-circle" color="warning" :value="true" dense class="v-alert-blink">
            <b>Identificamos arquivos vazios de 0 KB em seus documentos</b>
            <b>
              [{{ arquivosTamanhoZeroIdentificadosSPED ? 'SPED' : '' }}{{ arquivosTamanhoZeroIdentificadosCOGE ? ', COGE' : ''
              }}{{ arquivosTamanhoZeroIdentificadosSDI ? ', SDI' : '' }}]
            </b>
            , destacados em
            <b>laranja</b>
            na árvore de visualização à direita. Esses arquivos
            <b>podem causar erros no processamento do 'bulldozer'</b>
            .
            <b>Tem certeza de que deseja continuar?</b>
          </v-alert>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import BulldozerrService from '@/services/BulldozerrService.js';
  import ServerConfigService from '@/services/ServerConfigService.js';

  export default {
    components: {
      UploadFiles: () => import('@/components/general/UploadFiles')
    },

    props: {
      etapasSelecionadas: {
        type: Array,
        default: () => []
      }
    },

    emits: ['step-observer-files'],

    data: () => ({
      uploadMaxFilesize: '...',
      postMaxSize: null,
      maxExecutionTime: null,
      maxInputTime: null,
      search: '',
      uploadedFiles: [],
      files: {
        txt: 'mdi-file-document-outline',
        csv: 'mdi-google-spreadsheet'
      },
      caminhoTmp: null,
      loadDelete: false,
      activatable: true,
      defaultDirs: [
        {
          id: 1,
          name: 'SPED',
          isFolder: true,
          children: []
        },
        {
          id: 2,
          name: 'COGE',
          isFolder: true,
          children: []
        },
        {
          id: 3,
          name: 'SDI',
          isFolder: true,
          children: []
        },
        {
          id: 4,
          name: 'BDGD Anterior',
          isFolder: true,
          children: []
        },
        {
          id: 5,
          name: 'BDGD Atual',
          isFolder: true,
          children: []
        }
      ],
      selectedFolder: [0],
      initiallyOpen: [0]
    }),

    computed: {
      items() {
        return [
          {
            id: 0,
            name: 'Arquivos',
            children: this.defaultDirs.filter((item) => {
              switch (item.name) {
                case 'SPED':
                  return ['financeiro_do_icms', 'banco_de_compras', 'base_incremental'].some((e) =>
                    this.etapasSelecionadas.includes(e)
                  );
                case 'COGE':
                  return ['banco_de_compras', 'base_incremental', 'obrigacoes_especiais'].some((e) =>
                    this.etapasSelecionadas.includes(e)
                  );
                case 'SDI':
                  return [
                    'analise_ais',
                    'base_fisica',
                    'banco_de_compras',
                    'base_incremental',
                    'obrigacoes_especiais',
                    'base_blindada'
                  ].some((e) => this.etapasSelecionadas.includes(e));
                case 'BDGD Anterior':
                  return this.etapasSelecionadas.includes('base_fisica');
                case 'BDGD Atual':
                  return this.etapasSelecionadas.includes('base_fisica');
                default:
                  return false;
              }
            })
          }
        ];
      },

      functionUpload() {
        switch (this.selectedFolder[0]) {
          case 1:
            return require('../../../services/BulldozerrService').default.uploadFileIcms;
          case 2:
            return require('../../../services/BulldozerrService').default.uploadFileCoge;
          case 3:
            return require('../../../services/BulldozerrService').default.uploadFileSdi;
          case 4:
            return require('../../../services/BulldozerrService').default.uploadFileBdgdAnterior;
          case 5:
            return require('../../../services/BulldozerrService').default.uploadFileBdgdAtual;
          default:
            return null;
        }
      },

      validInputs() {
        const valid = this.items[0].children.filter((item) => {
          return item.children.length > 0;
        });

        return valid.length === this.items[0].children.length;
      },

      arquivosTamanhoZeroIdentificadosSPED() {
        const arquivosSPED = this.items[0].children.find((item) => item.name === 'SPED')?.children;

        return arquivosSPED?.some((arquivo) => arquivo.filesize == 0);
      },

      arquivosTamanhoZeroIdentificadosCOGE() {
        const arquivosCOGE = this.items[0].children.find((item) => item.name === 'COGE')?.children;

        return arquivosCOGE?.some((arquivo) => arquivo.filesize == 0);
      },

      arquivosTamanhoZeroIdentificadosSDI() {
        const arquivosSDI = this.items[0].children.find((item) => item.name === 'SDI')?.children;

        return arquivosSDI?.some((arquivo) => arquivo.filesize == 0);
      },

      arquivosTamanhoZeroIdentificadosBdgdAnterior() {
        const arquivosBdgdAnterior = this.items[0].children.find((item) => item.name === 'BDGD Anterior')?.children;

        return arquivosBdgdAnterior?.some((arquivo) => arquivo.filesize == 0);
      },

      arquivosTamanhoZeroIdentificadosBdgdAtual() {
        const arquivosBdgdAtual = this.items[0].children.find((item) => item.name === 'BDGD Atual')?.children;

        return arquivosBdgdAtual?.some((arquivo) => arquivo.filesize == 0);
      },

      arquivosTamanhoZeroIdentificados() {
        return (
          this.arquivosTamanhoZeroIdentificadosSPED ||
          this.arquivosTamanhoZeroIdentificadosCOGE ||
          this.arquivosTamanhoZeroIdentificadosSDI ||
          this.arquivosTamanhoZeroIdentificadosBdgdAnterior ||
          this.arquivosTamanhoZeroIdentificadosBdgdAtual
        );
      }
    },

    created() {
      this.getPhpConfig();
    },

    methods: {
      getPhpConfig() {
        ServerConfigService.getPhpConfig()
          .then((response) => {
            const { uploadMaxFilesize, postMaxSize, maxExecutionTime, maxInputTime } = response.data.info;
            this.uploadMaxFilesize = this.converterUnidades(uploadMaxFilesize);
            this.postMaxSize = this.converterUnidades(postMaxSize);
            this.maxExecutionTime = maxExecutionTime;
            this.maxInputTime = maxInputTime;
          })
          .catch((error) => console.error(error));
      },

      converterUnidades(input) {
        if (input === null || typeof input !== 'string') return input;

        if (input === '-1') return 'Sem limite';

        return input.replace(/G$/, 'GB').replace(/M$/, 'MB');
      },

      handleFileUploaded(event) {
        const files = event.files;

        this.caminhoTmp = files[0].caminho_tmp;

        const dirIndex = this.items[0].children.findIndex((item) => item.id === this.selectedFolder[0]);
        this.items[0].children[dirIndex].children = [];

        for (const index in files) {
          const itemFile = files[index];

          let objFile = {
            name: itemFile.nome,
            file: itemFile.extensao,
            filesize: itemFile.filesize,
            arquivo_id: itemFile.arquivo_id,
            path_id: dirIndex,
            id: Math.floor(5 + Math.random() * (99999 - 5))
          };

          this.items[0].children[dirIndex].children.push(objFile);
        }
      },

      deleteFile(item) {
        this.loadDelete = false;

        let relativePath = this.items[0].children[item.path_id].children;

        this.items[0].children[item.path_id].children = relativePath.filter((file) => file.arquivo_id != item.arquivo_id);

        BulldozerrService.deleteFile(item.arquivo_id).catch((err) => {
          console.error(err);
          this.$toast.error('Ocorreu um erro ao deletar o arquivo');
        });
      },

      isLoading(item) {
        if (this.selectedFolder[0] == 0) return false;

        let etapaLoadingID = null;

        this.items[0].children.forEach((i) => {
          if (etapaLoadingID) return null;
          if (i.id === this.selectedFolder[0]) etapaLoadingID = i.id;
          if (i.children.find((e) => e.id == this.selectedFolder[0])) etapaLoadingID = i.id;
        });

        return item.id === etapaLoadingID;
      }
    },

    watch: {
      validInputs(value) {
        this.$emit('step-observer-files', value);
      },

      items() {
        this.selectedFolder = [this.items[0].children.length ? this.items[0].children[0].id : 0];
      }
    }
  };
</script>

<style>
  .text-warning-bulldozer {
    color: #fb8c00 !important;
  }

  .treeview-bulldozerr .v-treeview-node__children .v-treeview-node__children {
    max-height: 350px !important;
    overflow-y: auto !important;
  }

  .not-dir-selected {
    display: flex;
    align-items: center;
    height: 100%;
  }

  .c-gray {
    color: gray;
  }

  .v-alert-blink {
    animation: blink 1s infinite alternate;
  }

  @keyframes blink {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0.8;
    }
  }

  .treeview-bulldozerr .v-treeview-node__root.v-treeview-node--active {
    pointer-events: none;
  }

  .treeview-bulldozerr .v-treeview-node__toggle {
    pointer-events: all;
  }
</style>
